import {
  addSetting,
  removeSettingByIndex,
  setSettingByIndex,
  setSettingByKey,
} from "@/features/commissionSlice";
import { setGlobalMessage } from "@/features/systemSlice";
import { getAllPromoter } from "@/fetch/fetchStaff";
import createExcelFile from "@/lib/createExcelFile";
import { enumerateDaysBetweenDates } from "@/lib/enumerateDaysBetweenDates";
import extractSpecialShift from "@/lib/extractSpecialShift";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import CloudDownload from "@mui/icons-material/CloudDownload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import _ from "lodash";
import moment from "moment";
import React from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

const MemoSpecialShiftFormRow = React.memo(
  function SpecialShiftFormRow({
    index,
    item,
    validations,
    disabled,
    removeFormData,
    promoterOptions,
    currentDate,
    handleFormData,
  }) {
    return (
      <Stack key={index} sx={{ my: 2 }} direction={"row"} spacing={2}>
        <Stack
          direction={"row"}
          justifyContent="space-between"
          alignItems={"flex-start"}
          sx={{ flex: 7 }}
          spacing={1}
        >
          <DatePicker
            sx={{ flex: 1 }}
            disabled={disabled}
            id="date"
            name="date"
            label="日期"
            minDate={moment(currentDate).startOf("month").format("YYYY-MM-DD")}
            maxDate={moment(currentDate).endOf("month").format("YYYY-MM-DD")}
            value={item.date}
            onChange={(value) => {
              handleFormData(index, "date", moment(value).format("YYYY-MM-DD"));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                error={!!validations[index]?.dateErr}
                helperText={validations[index]?.dateErr}
              />
            )}
          ></DatePicker>
          <Autocomplete
            sx={{ flex: 1 }}
            required
            disabled={disabled}
            id="combo-box-demo"
            options={promoterOptions}
            value={item.user}
            onChange={async (e, newValue) => {
              handleFormData(index, "user", newValue);
            }}
            label="員工"
            renderInput={(params) => <TextField {...params} label="員工" />}
          />
          <TextField
            disabled={true}
            value={item.rosterString}
            id="outlined-basic"
            label="更表"
            variant="outlined"
          />
          <TextField
            value={item.remark}
            sx={{ flex: 1 }}
            id="outlined-basic"
            error={!!validations[index]?.remarkErr}
            helperText={validations[index]?.remarkErr}
            label="備註"
            onChange={(event) =>
              handleFormData(index, "remark", event.target.value)
            }
            variant="outlined"
          />
        </Stack>
        <Stack
          direction={"row"}
          justifyContent="space-between"
          alignItems={"flex-start"}
          sx={{ flex: 1 }}
        >
          <IconButton color="error" onClick={() => removeFormData(index)}>
            <DeleteForeverIcon></DeleteForeverIcon>
          </IconButton>
        </Stack>
      </Stack>
    );
  },
  (prev, next) => {
    return (
      next.index === prev.index &&
      _.isEqual(prev.item, next.item) &&
      _.isEqual(prev.validations, next.validations)
    );
  },
);

export default function SpecialShiftFormRows({
  disabled,
  promoterOptions,
  handleRoster,
}) {
  const dispatch = useDispatch();

  const currentDate = useSelector(
    (state) => state.commission.settings.currentDate,
  );

  const formData = useSelector(
    (state) => state.commission.settings.specialShifts,
    shallowEqual,
  );

  const validations = useSelector(
    (state) => state.commission.validations.specialShifts,
    shallowEqual,
  );

  const removeFormData = React.useCallback(
    async (index) => {
      dispatch(
        removeSettingByIndex({
          key: "specialShifts",
          index: index,
        }),
      );
    },
    [dispatch],
  );
  const handleRosterRef = React.useRef(handleRoster);
  handleRosterRef.current = handleRoster;

  const handleDownloadTemplate = async () => {
    const dateRange = enumerateDaysBetweenDates(
      moment(currentDate).startOf("month"),
      moment(currentDate).endOf("month"),
    );
    const columns = [{ header: "Staff Code", key: "staff_code" }];
    dateRange.forEach((date) => {
      columns.push({
        header: date,
        key: date,
      });
    });
    createExcelFile(columns, "特別更範本");
  };

  const handleImportExcel = async (e, selectedDate) => {
    try {
      dispatch(
        setGlobalMessage({
          message: {
            severity: "info",
            message: "上載中.....",
            isError: false,
          },
        }),
      );
      let userData = {};
      const { data: users } = await getAllPromoter(true);
      _.forEach(users, (user) => {
        userData[user.staff_code.toLowerCase()] = {
          ...user,
        };
      });
      const specialShift = await extractSpecialShift(
        e.target.files[0],
        userData,
        selectedDate,
      );
      for (let index in specialShift) {
        specialShift[index] = await handleRosterRef.current(
          specialShift[index],
          specialShift[index]["user"].value,
          specialShift[index].date,
        );
      }
      dispatch(
        setSettingByKey({
          key: "specialShifts",
          value: _.concat(formData, specialShift),
        }),
      );
      dispatch(
        setGlobalMessage({
          message: {
            severity: "success",
            message: "上載成功",
            isError: false,
          },
        }),
      );
    } catch (error) {
      console.log("error", error);
      let message = "";
      switch (error.message) {
        case "dateIsNotCorrect":
          message = `上載失敗 \n 原因:日期不正確，數據必須於${moment(
            selectedDate,
          ).format("YYYY年M月")}內`;
          break;
        case "userNotExist":
          message = `上載失敗\n以下用戶不存在\n${error.id}`;
          break;
        default:
          message = "上載失敗";
          break;
      }
      dispatch(
        setGlobalMessage({
          message: {
            severity: "error",
            message: message,
            isError: true,
            code: 500,
          },
        }),
      );
    }
  };
  const handleFormData = React.useCallback(
    async (position, key, value) => {
      let cloneRow = { ...formData[position] };
      cloneRow[key] = value;
      if (
        cloneRow["user"] &&
        cloneRow.date &&
        (key === "date" || key === "user")
      ) {
        cloneRow = await handleRosterRef.current(
          cloneRow,
          cloneRow["user"].value,
          cloneRow.date,
        );
      }
      dispatch(
        setSettingByIndex({
          key: "specialShifts",
          value: cloneRow,
          index: position,
        }),
      );
    },
    [dispatch, formData],
  );
  const createFormData = React.useCallback(() => {
    dispatch(
      addSetting({
        key: "specialShifts",
        value: {
          date: moment(currentDate).format("YYYY-MM-DD"),
          roster: "FULL",
          rosterString: "",
          key: formData.length + 1,
          remark: "",
        },
      }),
    );
  }, [dispatch, currentDate, formData]);
  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });
  return (
    <React.Fragment>
      {!disabled && (
        <React.Fragment>
          <Box
            sx={{
              display: "flex",
              gridTemplateColumns: { sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <Button
              component="label"
              size="small"
              color={"success"}
              role={undefined}
              variant={"contained"}
              startIcon={<CloudDownload />}
              onClick={() => handleDownloadTemplate()}
            >
              下載範本
            </Button>
            <Button
              component="label"
              size="small"
              role={undefined}
              variant={"contained"}
              startIcon={<CloudUploadIcon />}
            >
              上載
              <VisuallyHiddenInput
                type="file"
                onChange={(event) => handleImportExcel(event, currentDate)}
              />
            </Button>
          </Box>
        </React.Fragment>
      )}
      {_.map(formData, (item, index) => (
        <MemoSpecialShiftFormRow
          key={item.id}
          index={index}
          item={item}
          validations={validations}
          disabled={disabled}
          removeFormData={removeFormData}
          promoterOptions={promoterOptions}
          currentDate={currentDate}
          handleFormData={handleFormData}
        ></MemoSpecialShiftFormRow>
      ))}
      {!disabled && (
        <Box>
          <IconButton component="label" onClick={createFormData}>
            <AddBoxOutlinedIcon />
          </IconButton>
        </Box>
      )}
    </React.Fragment>
  );
}
